import { baseApi } from './baseApi';

const toQuery = (filter = {}) => {
  const p = new URLSearchParams();
  Object.entries(filter).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.append(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const patientsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listPatients: build.query({
      query: (filter) => `/patients${toQuery(filter)}`,
      transformResponse: (resp) => resp.data,
      // ALWAYS provide the LIST tag (not only when empty) — otherwise `invalidatesTags: LIST`
      // from create/delete mutations never re-fetches a non-empty list.
      providesTags: (result) => [
        { type: 'Patient', id: 'LIST' },
        ...(result?.items?.map((p) => ({ type: 'Patient', id: p.id })) ?? []),
      ],
    }),
    // "Sessions left & no booking" — remaining sessions > 0, nothing booked, and last completed
    // session either exactly `days` ago or within the from/to range (one date = that exact day).
    sessionIdlePatients: build.query({
      query: ({ days = 3, from, to } = {}) => {
        const p = new URLSearchParams();
        if (from || to) {
          if (from) p.append('from', from);
          if (to) p.append('to', to);
        } else {
          p.append('days', days);
        }
        return `/patients/session-idle?${p.toString()}`;
      },
      transformResponse: (resp) => resp.data,
      providesTags: [
        { type: 'Patient', id: 'LIST' }, { type: 'Appointment', id: 'LIST' }, { type: 'Invoice', id: 'LIST' },
      ],
    }),
    getPatient: build.query({
      query: (id) => `/patients/${id}`,
      transformResponse: (resp) => resp.data,
      providesTags: (_r, _e, id) => [{ type: 'Patient', id }],
    }),
    getPatientSummary: build.query({
      query: (id) => `/patients/${id}/summary`,
      transformResponse: (resp) => resp.data,
      providesTags: (_r, _e, id) => [{ type: 'Patient', id }],
    }),
    // Per-procedure session balances (bought / completed / remaining), computed live on the server
    // from billing + appointment history. `remaining` is unclamped — negative means over-used.
    getPatientSessionLedger: build.query({
      query: (id) => `/patients/${id}/session-ledger`,
      transformResponse: (resp) => resp.data,
      // Derived from bills AND visits, so any change to either must refresh it.
      providesTags: (_r, _e, id) => [
        { type: 'SessionLedger', id },
        { type: 'Appointment', id: 'LIST' },
        { type: 'Invoice', id: 'LIST' },
      ],
    }),

    // Procedures the patient may be booked for right now: everything on their latest bill (which
    // can go negative) plus older purchases with sessions left. Populates the booking dropdown.
    getBookableProcedures: build.query({
      query: (id) => `/patients/${id}/bookable-procedures`,
      transformResponse: (resp) => resp.data,
      providesTags: (_r, _e, id) => [
        { type: 'SessionLedger', id },
        { type: 'Invoice', id: 'LIST' },
      ],
    }),

    getPatientOverview: build.query({
      query: (id) => `/patients/${id}/overview`,
      transformResponse: (resp) => resp.data,
      // Depends on appointments/sessions/invoices too, so refresh when those change.
      providesTags: (_r, _e, id) => [
        { type: 'Patient', id },
        { type: 'Appointment', id: 'LIST' },
        { type: 'Session', id: 'LIST' },
        { type: 'Invoice', id: 'LIST' },
      ],
    }),
    createPatient: build.mutation({
      query: (body) => ({ url: '/patients', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: [{ type: 'Patient', id: 'LIST' }, 'Dashboard'],
    }),
    updatePatient: build.mutation({
      query: ({ id, ...body }) => ({ url: `/patients/${id}`, method: 'PUT', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Patient', id }, { type: 'Patient', id: 'LIST' }],
    }),
    // Engagement / renewal-tracking fields (Google review, feedback form, renewal pitched + by whom).
    // Used by both the Patient profile drawer AND the appointment calendar's popover for
    // frictionless one-tap updates from anywhere a patient is referenced.
    updatePatientEngagement: build.mutation({
      query: ({ id, ...body }) => ({ url: `/patients/${id}/engagement`, method: 'PATCH', body }),
      transformResponse: (resp) => resp.data,
      // Optimistic update — the checkbox flips immediately even before the server confirms.
      // Rolls back on error.
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
        const undo = dispatch(
          patientsApi.util.updateQueryData('getPatient', id, (draft) => {
            if (draft) Object.assign(draft, patch);
          }),
        );
        try { await queryFulfilled; } catch { undo.undo(); }
      },
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Patient', id }, { type: 'Patient', id: 'LIST' }],
    }),
    // Manual session management: set TotalSessions and (optionally) override the used count.
    updatePatientSessions: build.mutation({
      query: ({ id, ...body }) => ({ url: `/patients/${id}/sessions`, method: 'PATCH', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Patient', id }, { type: 'Patient', id: 'LIST' }],
    }),
    // Counts-only "here's what will disappear" preview, fetched to populate the delete-confirmation dialog.
    getPatientDeletePreview: build.query({
      query: (id) => `/patients/${id}/delete-preview`,
      transformResponse: (resp) => resp.data,
    }),
    // Admin-only: cascades through the patient's invoices/appointments/sessions/etc. (see backend
    // PatientService.DeleteAsync) before soft-deleting the patient record itself.
    deletePatient: build.mutation({
      query: (id) => ({ url: `/patients/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Patient', id }, { type: 'Patient', id: 'LIST' },
        { type: 'Appointment', id: 'LIST' }, { type: 'Invoice', id: 'LIST' }, { type: 'Session', id: 'LIST' },
        'Dashboard',
      ],
    }),
  }),
});

export const {
  useListPatientsQuery,
  useLazyListPatientsQuery,
  useSessionIdlePatientsQuery,
  useGetPatientQuery,
  useGetPatientSummaryQuery,
  useGetPatientOverviewQuery,
  useGetPatientSessionLedgerQuery,
  useGetBookableProceduresQuery,
  useCreatePatientMutation,
  useUpdatePatientMutation,
  useUpdatePatientSessionsMutation,
  useUpdatePatientEngagementMutation,
  useLazyGetPatientDeletePreviewQuery,
  useDeletePatientMutation,
} = patientsApi;
